import React, { useState } from 'react';
import {
    ArrowDownTrayIcon,
    DocumentArrowDownIcon,
    TableCellsIcon,
    SparklesIcon,
} from '@heroicons/react/24/outline';
import { AdminDashboardService } from '../../services/adminDashboard.service';
import { useNotification } from '../../contexts/NotificationContext';

interface ExportReportsProps {
    startDate?: string;
    endDate?: string;
    sections?: string[];
}

export const ExportReports: React.FC<ExportReportsProps> = ({
    startDate,
    endDate,
    sections,
}) => {
    const [exporting, setExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState<string | null>(null);
    const { showNotification } = useNotification();

    const handleExport = async (format: 'csv' | 'excel' | 'json') => {
        try {
            setExporting(true);
            setExportFormat(format);

            const reportConfig = {
                format,
                startDate: startDate ? new Date(startDate) : undefined,
                endDate: endDate ? new Date(endDate) : undefined,
                sections,
                includeCharts: false,
            };

            const reportData = await AdminDashboardService.exportReport(reportConfig);

            // Create blob and download
            let blob: Blob;
            let filename: string;

            if (format === 'json') {
                blob = new Blob([reportData as string], { type: 'application/json' });
                filename = `admin-report-${new Date().toISOString().split('T')[0]}.json`;
            } else if (format === 'csv') {
                blob = new Blob([reportData as string], { type: 'text/csv' });
                filename = `admin-report-${new Date().toISOString().split('T')[0]}.csv`;
            } else {
                blob = new Blob([reportData as ArrayBuffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                filename = `admin-report-${new Date().toISOString().split('T')[0]}.xlsx`;
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showNotification(`Report exported successfully as ${format.toUpperCase()}`, 'success');
        } catch (error: unknown) {
            console.error('Error exporting report:', error);
            const errorMessage = error && typeof error === 'object' && 'response' in error
                ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
                : undefined;
            showNotification(
                errorMessage || 'Failed to export report',
                'error'
            );
        } finally {
            setExporting(false);
            setExportFormat(null);
        }
    };

    const exportButtons = [
        {
            format: 'csv' as const,
            label: 'Export CSV',
            icon: TableCellsIcon,
            gradientClasses: 'bg-gradient-to-r from-[#06ec9e] via-emerald-500 to-[#009454] dark:from-[#06ec9e] dark:via-emerald-600 dark:to-[#009454] hover:from-[#22c55e] hover:via-emerald-600 hover:to-[#16a34a] dark:hover:from-[#22c55e] dark:hover:via-emerald-700 dark:hover:to-[#16a34a] shadow-lg shadow-[#06ec9e]/30 dark:shadow-[#06ec9e]/40 hover:shadow-[#06ec9e]/50 dark:hover:shadow-[#06ec9e]/60',
            description: 'Comma-separated values',
        },
        {
            format: 'excel' as const,
            label: 'Export Excel',
            icon: ArrowDownTrayIcon,
            gradientClasses: 'bg-gradient-to-r from-[#06ec9e] via-emerald-500 to-[#009454] dark:from-[#06ec9e] dark:via-emerald-600 dark:to-[#009454] hover:from-[#22c55e] hover:via-emerald-600 hover:to-[#16a34a] dark:hover:from-[#22c55e] dark:hover:via-emerald-700 dark:hover:to-[#16a34a] shadow-lg shadow-[#06ec9e]/30 dark:shadow-[#06ec9e]/40 hover:shadow-[#06ec9e]/50 dark:hover:shadow-[#06ec9e]/60',
            description: 'Excel spreadsheet',
        },
        {
            format: 'json' as const,
            label: 'Export JSON',
            icon: DocumentArrowDownIcon,
            gradientClasses: 'bg-gradient-to-r from-[#06ec9e] via-emerald-500 to-[#009454] dark:from-[#06ec9e] dark:via-emerald-600 dark:to-[#009454] hover:from-[#22c55e] hover:via-emerald-600 hover:to-[#16a34a] dark:hover:from-[#22c55e] dark:hover:via-emerald-700 dark:hover:to-[#16a34a] shadow-lg shadow-[#06ec9e]/30 dark:shadow-[#06ec9e]/40 hover:shadow-[#06ec9e]/50 dark:hover:shadow-[#06ec9e]/60',
            description: 'JSON data format',
        },
    ];

    return (
        <div className="glass p-4 sm:p-5 md:p-6 shadow-2xl backdrop-blur-xl border border-primary-200/30 rounded-2xl bg-gradient-to-br from-white/90 to-white/70 dark:from-dark-card/90 dark:to-dark-card/70">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 md:mb-6">
                <div className="p-1.5 sm:p-2 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg">
                    <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                    <h3 className="text-base sm:text-lg font-display font-bold gradient-text">
                        Export Reports
                    </h3>
                    <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary font-body">
                        Download dashboard data
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
                {exportButtons.map((button) => {
                    const Icon = button.icon;
                    const isExporting = exporting && exportFormat === button.format;

                    return (
                        <button
                            key={button.format}
                            onClick={() => handleExport(button.format)}
                            disabled={exporting}
                            className={`
                                group relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 md:py-3 rounded-xl 
                                font-display font-semibold text-white text-xs sm:text-sm
                                transition-all duration-300 
                                disabled:opacity-50 disabled:cursor-not-allowed 
                                hover:scale-105 active:scale-95
                                shadow-lg hover:shadow-xl 
                                overflow-hidden
                                ${isExporting
                                    ? 'bg-gradient-to-r from-[#06ec9e] via-emerald-500 to-[#009454] dark:from-[#06ec9e] dark:via-emerald-600 dark:to-[#009454] shadow-lg shadow-[#06ec9e]/30 dark:shadow-[#06ec9e]/40'
                                    : button.gradientClasses
                                }
                            `}
                            title={button.description}
                        >
                            {isExporting ? (
                                <>
                                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span className="text-white font-medium">Exporting...</span>
                                </>
                            ) : (
                                <>
                                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                    <span className="text-white font-medium">{button.label}</span>
                                </>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
